import React, { useEffect, useRef, useState } from "react";
import { FiArrowUp, FiArrowDown, FiMinus } from "react-icons/fi";
import Chart from "chart.js/auto";

export default function KpiCard({ title, value, unit, target, trend = [] }) {
    const chartRef = useRef(null);
    const [selectedPeriod, setSelectedPeriod] = useState("1w");

    const periods = ["1w", "1m", "3m", "6m", "1y"];

    const getFilteredTrend = () => {
        switch (selectedPeriod) {
            case "1w": return trend.slice(-7);
            case "1m": return trend.slice(-30);
            case "3m": return trend.slice(-90);
            case "6m": return trend.slice(-180);
            case "1y": return trend.slice(-365);
            default: return trend;
        }
    };

    const filteredTrend = getFilteredTrend();

    // Status calculation
    let statusType = "stable";
    let difference = 0;
    if (filteredTrend.length >= 2) {
        const last = filteredTrend[filteredTrend.length - 1];
        const prev = filteredTrend[filteredTrend.length - 2];
        difference = last - prev;
        if (difference > 0) statusType = "Increasing";
        else if (difference < 0) statusType = "Decreasing";
    }

    // 👉 Detect if this KPI is about deaths
    const isDeathCard = title.toLowerCase().includes("death");

    // 1. Define the base color maps (Positive Health = Green/Good, Negative Health = Red/Bad)
    const POSITIVE_COLORS = {
        Increasing: "text-green-600 bg-green-50",
        Decreasing: "text-red-600 bg-red-50",
        stable: "text-slate-600 bg-slate-100",
    };

    const NEGATIVE_COLORS = {
        Increasing: "text-red-600 bg-red-50",     // Flipped: Increasing is BAD (Red)
        Decreasing: "text-green-600 bg-green-50", // Flipped: Decreasing is GOOD (Green)
        stable: "text-slate-600 bg-slate-100",
    };

    const CHART_COLORS = {
        Increasing: { border: "#22C55E", background: "rgba(34,197,94,0.15)" },
        Decreasing: { border: "#EF4444", background: "rgba(239,68,68,0.15)" },
        stable: { border: "#64748B", background: "rgba(100,116,139,0.15)" },
    };
    
    // Flipped Chart Colors for Death cards
    const CHART_COLORS_DEATH = {
        Increasing: { border: "#EF4444", background: "rgba(239,68,68,0.15)" },     // Flipped: Red for increasing
        Decreasing: { border: "#22C55E", background: "rgba(34,197,94,0.15)" },    // Flipped: Green for decreasing
        stable: { border: "#64748B", background: "rgba(100,116,139,0.15)" },
    };


    // 2. Select the final color maps based on isDeathCard
    const statusColors = isDeathCard ? NEGATIVE_COLORS : POSITIVE_COLORS;
    const chartColorMap = isDeathCard ? CHART_COLORS_DEATH : CHART_COLORS;


    // 👉 Replace wording only for death counts (Keep this logic)
    const statusTextMap = {
        Increasing: isDeathCard ? "Increasing" : "Increasing",
        Decreasing: isDeathCard ? "Decreasing" : "Decreasing",
        stable: "Stable",
    };

    const arrowIcons = {
        Increasing: <FiArrowUp size={14} />,
        Decreasing: <FiArrowDown size={14} />,
        stable: <FiMinus size={14} />,
    };

    // Chart rendering
    useEffect(() => {
        if (!chartRef.current || filteredTrend.length === 0) return;

        // Get the specific colors for the current status type
        const { border, background } = chartColorMap[statusType] || chartColorMap['stable'];

        const ctx = chartRef.current.getContext("2d");
        const chartInstance = new Chart(ctx, {
            type: "line",
            data: {
                labels: filteredTrend.map((_, i) => i + 1),
                datasets: [{
                    data: filteredTrend,
                    fill: true,
                    // 3. Apply the selected colors
                    borderColor: border,
                    backgroundColor: background,
                    tension: 0.3,
                    borderWidth: 2,
                    pointRadius: 0,
                }],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: { legend: { display: false }, tooltip: { enabled: true }, datalabels: false },
                scales: { x: { display: false }, y: { display: false } },
            },
        });

        return () => chartInstance.destroy();
    }, [filteredTrend, statusType, isDeathCard]); // Added isDeathCard to dependency array

    return (
        <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm w-full flex flex-col justify-between" >
            
            {/* Title */}
            <div className="text-[12px] text-slate-500 font-medium truncate">{title}</div>

            {/* Value + Status */}
            <div className="flex items-center justify-between mt-1">
                <h3 className="text-xl font-semibold text-gray-800 truncate">
                    {value}{unit && <span className="text-sm text-slate-500 ml-1">{unit}</span>}
                </h3>

                {/* 4. Use the selected statusColors */}
                <div className={`flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium ${statusColors[statusType]}`}>
                    {arrowIcons[statusType]}
                    {statusTextMap[statusType]} ({difference >= 0 ? "+" : ""}{difference})
                </div>
            </div>
            {target &&
                <div className="text-[11px] text-slate-400 mt-1 truncate">
                    Target: <span className="font-medium text-slate-500">{target}</span>
                </div>
            }

            {/* Chart */}
            {filteredTrend.length > 0 && (
                <div className="mt-2 h-14 w-full">
                    <canvas ref={chartRef}></canvas>
                </div>
            )}

            {/* Period buttons */}
            <div className="mt-2 flex w-full border border-slate-300 rounded overflow-hidden divide-x divide-slate-300 text-center text-xs">
                {periods.map((p) => (
                    <button
                        key={p}
                        onClick={() => setSelectedPeriod(p)}
                        className={`flex-1 py-1 font-medium transition-colors duration-150 ${
                            selectedPeriod === p
                                ? "text-blue-600 border-b-2 border-blue-600"
                                : "text-slate-700 hover:bg-slate-100"
                        }`}
                    >
                        {p}
                    </button>
                ))}
            </div>
        </div>
    );
}